import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/shared/Sidebar';
import Header from '../../components/shared/Header';
import DocumentFilters from '../../components/documents/DocumentFilters';
import DocumentTable from '../../components/documents/DocumentTable';
import DocumentViewModal from '../../components/documents/DocumentViewModal';
import BulkActions from '../../components/documents/BulkActions';
import { apiService, Document, ApiError } from '../../services/api';
import './Documents.css';

const Documents: React.FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('All Types');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [searchQuery, setSearchQuery] = useState('');
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewDocument, setViewDocument] = useState<Document | null>(null);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const fetchDocuments = async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getAllDocuments(signal);

      let documentsArray: any[] = [];
      if (response.documents && Array.isArray(response.documents)) {
        documentsArray = response.documents;
      } else if (response.data && Array.isArray(response.data)) {
        documentsArray = response.data;
      } else if (Array.isArray(response)) {
        documentsArray = response;
      }

      if (documentsArray.length > 0) {
        const mappedDocuments = documentsArray.map(apiDocument =>
          apiService.mapApiDocumentToDocument(apiDocument)
        );
        setDocuments(mappedDocuments);
      } else {
        setError('No documents found');
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;

      if (err instanceof ApiError) {
        if (err.status === 403) {
          setError('You do not have permission to view documents. Please contact your administrator.');
        } else if (err.status === 401) {
          setError('Your session has expired. Please sign in again.');
        } else {
          setError(err.message || 'An error occurred while fetching documents');
        }
      } else {
        setError(err.message || 'An error occurred while fetching documents');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    fetchDocuments(abortController.signal);
    return () => abortController.abort();
  }, []);

  const totalDocuments = documents.length;
  const documentsPerPage = 8;
  const totalPages = Math.ceil(totalDocuments / documentsPerPage);

  // Filter documents
  const filteredDocuments = documents.filter(document => {
    const matchesSearch = 
      document.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      document.restaurant.toLowerCase().includes(searchQuery.toLowerCase()) ||
      document.documentType.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesType = typeFilter === 'All Types' || 
      document.documentType === typeFilter;

    const matchesStatus = statusFilter === 'All Status' || 
      document.status === statusFilter.toLowerCase().replace(' ', '-');

    return matchesSearch && matchesType && matchesStatus;
  });

  // Calculate summary stats
  const pendingCount = filteredDocuments.filter(d => d.status === 'pending').length;
  const expiringSoonCount = 1; // This would come from actual data

  const handleSelectDocument = (documentId: string) => {
    setSelectedDocuments(prev =>
      prev.includes(documentId)
        ? prev.filter(id => id !== documentId)
        : [...prev, documentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedDocuments.length === filteredDocuments.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(filteredDocuments.map(d => d.id));
    }
  };

  const handleBulkApprove = () => {
    if (selectedDocuments.length === 0) {
      alert('Please select documents to approve');
      return;
    }
    console.log('Bulk approve:', selectedDocuments);
    // TODO: Implement bulk approve
    setSelectedDocuments([]);
  };

  const handleBulkReject = () => {
    if (selectedDocuments.length === 0) {
      alert('Please select documents to reject');
      return;
    }
    console.log('Bulk reject:', selectedDocuments);
    // TODO: Implement bulk reject
    setSelectedDocuments([]);
  };

  const handleAssignReviewer = () => {
    if (selectedDocuments.length === 0) {
      alert('Please select documents to assign reviewer');
      return;
    }
    console.log('Assign reviewer to:', selectedDocuments);
    // TODO: Implement assign reviewer
  };

  const handleApproveDocument = async (document: Document): Promise<boolean> => {
    if (document.rawId == null) {
      alert('Cannot approve: missing document ID');
      return false;
    }
    try {
      await apiService.updateDocumentStatus(document.rawId, { status: 'approved' });
      await fetchDocuments();
      return true;
    } catch (err: any) {
      if (err instanceof ApiError) {
        alert(err.message || 'Failed to approve document');
      } else {
        alert('Failed to approve document. Please try again.');
      }
      return false;
    }
  };

  const handleRejectDocument = async (document: Document): Promise<boolean> => {
    if (document.rawId == null) {
      alert('Cannot reject: missing document ID');
      return false;
    }
    try {
      await apiService.updateDocumentStatus(document.rawId, {
        status: 'rejected',
        rejectionReason: 'Rejected from admin panel',
      });
      await fetchDocuments();
      return true;
    } catch (err: any) {
      if (err instanceof ApiError) {
        alert(err.message || 'Failed to reject document');
      } else {
        alert('Failed to reject document. Please try again.');
      }
      return false;
    }
  };

  const handleApproveFromModal = async (document: Document) => {
    const success = await handleApproveDocument(document);
    if (success) setViewDocument(null);
  };

  const handleRejectFromModal = async (document: Document) => {
    const success = await handleRejectDocument(document);
    if (success) setViewDocument(null);
  };

  // Paginate filtered documents
  const startIndex = (currentPage - 1) * documentsPerPage;
  const endIndex = startIndex + documentsPerPage;
  const paginatedDocuments = filteredDocuments.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, typeFilter, statusFilter]);

  return (
    <div className="dashboard-container">
      <Sidebar 
        collapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
      />
      <div className={`dashboard-main ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Header page="documents" />
        <main className="dashboard-content">
          <div className="documents-header-section">
            <div>
              <h1 className="documents-title">Document Management</h1>
              <div className="documents-breadcrumbs">
                <span>Home</span>
                <span className="breadcrumb-separator">›</span>
                <span>Compliance</span>
                <span className="breadcrumb-separator">›</span>
                <span>Documents</span>
              </div>
              <p className="documents-summary">
                {pendingCount} pending, {expiringSoonCount} expiring soon
              </p>
            </div>
          </div>

          <BulkActions
            selectedCount={selectedDocuments.length}
            onBulkApprove={handleBulkApprove}
            onBulkReject={handleBulkReject}
            onAssignReviewer={handleAssignReviewer}
          />

          <DocumentFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            typeFilter={typeFilter}
            onTypeFilterChange={setTypeFilter}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
          />

          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <p>Loading documents...</p>
            </div>
          ) : error ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>
              <p>Error: {error}</p>
            </div>
          ) : (
            <>
            <DocumentTable
              documents={paginatedDocuments}
              selectedDocuments={selectedDocuments}
              onSelectDocument={handleSelectDocument}
              onSelectAll={handleSelectAll}
              onView={setViewDocument}
              onApprove={handleApproveDocument}
              onReject={handleRejectDocument}
            />

            <DocumentViewModal
              document={viewDocument}
              onClose={() => setViewDocument(null)}
              onApprove={handleApproveFromModal}
              onReject={handleRejectFromModal}
            />

              {totalDocuments > 0 && (
            <div className="documents-pagination">
              <div className="pagination-info">
                Showing {((currentPage - 1) * documentsPerPage) + 1}-{Math.min(currentPage * documentsPerPage, totalDocuments)} of {totalDocuments}
              </div>
              <div className="pagination-controls">
                <button
                  className="pagination-btn"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => prev - 1)}
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(2, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  className="pagination-btn"
                  disabled={currentPage === totalPages || totalPages === 0}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
            </>
          )}
          {!loading && !error && totalDocuments === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <p>No documents found.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Documents;

